import { useState } from "react";
import {
  formatPercentPoints,
  normalizeOriginationFeePct,
  parseDecimalLike,
} from "../originationFee";
import type { OriginationInput } from "./OfferTerms";

/**
 * The origination fee % field's state: what is typed stays as typed ("2." on the way to "2.5")
 * while each value that parses is committed (a value past the fee's range shows as the one kept);
 * on blur, and on the stepper, the value is settled,
 * formatted, and snapped to `step` when given. Text that does not parse goes back, on blur, to
 * the fee in force (`pct`), which is what a send uses.
 */
export function useOriginationInput(
  pct: number,
  commit: (pct: number) => void,
  step?: number
): OriginationInput {
  const [typed, setTyped] = useState<string | null>(null);
  const settle = (raw: number) => {
    const snapped = step ? Math.round(raw / step) * step : raw;
    setTyped(null);
    commit(normalizeOriginationFeePct(snapped));
  };
  return {
    pct,
    input: typed ?? formatPercentPoints(pct),
    onInput: (raw) => {
      const next = parseDecimalLike(raw);
      if (next == null) {
        setTyped(raw);
        return;
      }
      const kept = normalizeOriginationFeePct(next);
      // Past what a fee can be ("30"), the box shows what is kept (25), never a value not used.
      setTyped(kept === next ? raw : formatPercentPoints(kept));
      commit(kept);
    },
    onStep: (delta) => settle(pct + delta),
    onBlur: () => {
      if (typed == null) return;
      const next = parseDecimalLike(typed);
      if (next == null) setTyped(null);
      else settle(next);
    },
  };
}
